import { AfterViewInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';

import { MatMenuTrigger } from '@angular/material/menu';

import { translate } from '@jsverse/transloco';
import { v4 as UUIDv4 } from 'uuid';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { Constants } from '../../../../constants/constants';
import { Filter, WidgetQueryOptions } from '../../../collection/services/filter.service';
import { AnalyticItemTypes } from '../../../dashboard/services/analytic-item-types';
import {
  TaxonomySearchDialogComponent,
  TaxonomySearchDialogOptions,
} from '../taxonomy-search/taxonomy-search-dialog.component';
import { WidgetChartComponent, WidgetChartOptions } from '../widget-chart/widget-chart.component';
import { ChartAiInsightPointSelectionService } from '../widget-expansion-panel/widget-ai-insights/chart-ai-insight-point-selection.service';
import { ChartWidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/chart-widget-ai-insights-validator.service';
import { WidgetAiInsightsValidatorService } from '../widget-expansion-panel/widget-ai-insights/services/validation/widget-ai-insights-validator.service';
import { TaxonomyMapChartReport } from '../../services/chart-report';

export interface TaxonomyMapWidgetOptions extends WidgetChartOptions {
  prevTaxonomyLevels?: string[];
  selectedNode?: string;
  taxonomyPath?: string;
  cloudFromTaxonomy?: boolean;
  topLevelMentionItem?: string;
}

export interface TaxonomyLevel {
  id: string;
  path: string;
  name: string;
  depth: number;
}

@EnableClassTelemetry()
@Component({
  selector: 'app-widget-taxonomy-map',
  templateUrl: './widget-taxonomy-map.component.html',
  styleUrls: ['./widget-taxonomy-map.component.scss'],
  providers: [
    ChartAiInsightPointSelectionService,
    { provide: WidgetAiInsightsValidatorService, useClass: ChartWidgetAiInsightsValidatorService },
  ],
  standalone: false,
})
export class WidgetTaxonomyMapComponent
  extends WidgetChartComponent<TaxonomyMapWidgetOptions>
  implements OnInit, AfterViewInit
{
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  public report: TaxonomyMapChartReport;
  public downloadFormats = ['pptx', 'image'];
  public dateIntervals = ['day', 'week', 'month', 'year'];
  public modelDateTopFrom: string;
  canFilter = true;
  hasAnalyses = true;
  public minimumOccurences: number;
  private viewedDocument = false;

  @ViewChild(MatMenuTrigger, { static: true })
  contextMenuTriggerElement: MatMenuTrigger;
  @ViewChild(MatMenuTrigger, { read: ElementRef, static: true })
  contextMenuTriggerNativeElement: ElementRef;

  ngOnInit() {
    super.ngOnInit();
    this.setWidgetGroup(this.dashboardViewComponent.getWidgetWidgetGroup(this.widget.id));
    this.widget.payload.prevTaxonomyLevels = this.widget.payload.prevTaxonomyLevels
      ? this.widget.payload.prevTaxonomyLevels
      : [];
    this.pointHasDocuments = this.hasPointDocuments();
    if (this.isSmartSummaryEnabled) {
      this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
      this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
    }
  }

  async ngAfterViewInit() {
    await super.ngAfterViewInit();
  }

  public get hasTaxonomyLevel(): boolean {
    return !!this.report && this.report.hasTaxonomyLevel();
  }

  async refresh(resetUserDateInterval = false, initialLoad = false): Promise<any> {
    this.isLoading = true;
    this.widget.payload.taxonomyPath ??= '';
    await super.refresh(resetUserDateInterval, initialLoad);
    this.resetTaxonomyLevel();
  }

  protected async executeReport(format, drillDown?, queryForDocuments?) {
    let response;
    switch (drillDown) {
      case 'taxonomy_mentions':
        response = await this.report.executeReportForTaxonomyMentions(
          this.taxonomyFullPathPoint(),
          format,
          queryForDocuments
        );
        break;
      case 'taxonomy_phrases':
        response = await this.report.executeReportForFeaturePhrases(
          this.pointName,
          'topics',
          format,
          queryForDocuments,
          this.taxonomyFullPathPoint()
        );
        break;
      case 'taxonomy_themes':
        response = await this.report.executeReportForThemes(
          'topics',
          format,
          queryForDocuments,
          this.taxonomyFullPathPoint()
        );
        break;
      case 'taxonomy':
        response = await this.report.executeReportForTaxonomy(
          this.widget.payload.taxonomyPath,
          format,
          queryForDocuments
        );
        break;
      default:
        response = await this.report.executeReport(format, queryForDocuments);
    }

    return response;
  }

  protected async exportChartToPptx(pptx, widgetTitle, imageOptions?, slide?) {
    const blob = await this.chartDisplayService.exportChartAsImage(
      this.elementContent.nativeElement
    );
    return await this.exportImageToPptx(blob, pptx, this.displayTaxonomyPath, imageOptions, slide);
  }

  resetTaxonomyLevel(force = false) {
    if (force || (this.viewedDocument && this.payload.type === 'taxonomyMap')) {
      this.viewedDocument = false;
      this.popTaxonomyLevel();
    }
  }

  public widgetContextMenuOpen(event: Highcharts.PointerEventObject): void {
    if (this.widgetComponent.showCommentList) {
      return;
    }

    setTimeout(() => {
      this.contextMenuTriggerNativeElement.nativeElement.setAttribute(
        'style',
        `left: ${event.chartX}px; top: ${event.chartY}px`
      );
      this.point = event['point'];
      this.widgetContextMenu = { ...event['point'] };

      this.setTaxonomyNodeInfo();
      this.pointComments = this.report.getCommentsForPoint(this.point, this.payload.comments);
      this.pointSummaries = this.getSmartSummariesForPoint(this.pointName);
      this.pointHasDocuments = this.hasPointDocuments();
      if (this.isSmartSummaryEnabled) {
        this.interactiveSmartSummaryMenu = this.buildSmartSummaryMenu();
        this.removeInteractiveSmartSummaryMenu = this.buildSmartSummaryRemoveMenu();
      }
      this.canCreateSmartSummary = this.isSmartSummaryEnabledForPoint;

      this.downloadSmartSummaryRequestToolTip = !this.canCreateSmartSummary
        ? translate('widgets.smart-summary.unavailable-for-points-with-less-than-documents', {
            count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
          })
        : '';

      this.contextMenuTriggerElement.openMenu();
    });
  }

  public setTaxonomyNodeInfo() {
    if (!this.widgetContextMenu || !this.widgetContextMenu.options) {
      return '';
    }
    this.pointName = this.widgetContextMenu.options.pointId;
    this.pointDisplayName = this.widgetContextMenu.options.pointDisplay;
  }

  popTaxonomyLevel() {
    const poppedLevel =
      this.widget.payload.prevTaxonomyLevels && this.widget.payload.prevTaxonomyLevels.length > 0
        ? this.widget.payload.prevTaxonomyLevels.pop()
        : '';
    this.widget.payload.taxonomyPath = poppedLevel;
  }

  public get canDrilldownTaxonomy(): boolean {
    if (this.payload.type !== 'taxonomyMap') {
      return false;
    }
    if (!this.widgetContextMenu) {
      return false;
    }
    return !this.widgetContextMenu.options.isLeaf;
  }

  public get canDrilldownTaxonomyMentionsAndPhrasesAndThemes(): boolean {
    if (this.payload.type !== 'taxonomyMap') {
      return false;
    }
    if (!this.widgetContextMenu) {
      return false;
    }
    return this.widgetContextMenu.options.isLeaf;
  }

  private calculateTaxonomyPath() {
    if (
      this.widget.payload.prevTaxonomyLevels &&
      this.widget.payload.prevTaxonomyLevels.length === 0
    ) {
      this.widget.payload.taxonomyPath = '';
    } else if (!this.widget.payload.taxonomyPath || this.widget.payload.taxonomyPath === '') {
      this.widget.payload.taxonomyPath = this.pointDisplayName;
    } else {
      this.widget.payload.taxonomyPath =
        this.widget.payload.taxonomyPath + '|' + this.pointDisplayName;
    }
  }

  private setTaxonomyLevels(item, isRoot = false) {
    this.popTaxonomyLevel();

    if (
      this.widget.payload.prevTaxonomyLevels &&
      this.widget.payload.prevTaxonomyLevels.length <= 0
    ) {
      this.widget.payload.drilledDownOn = item;
      this.widget.payload.isDrilledDown = false;
      this.widget.payload.drilledDownLocation = '';
    } else {
      this.widget.payload.drilledDownLocation =
        this.widget.payload.taxonomyPath?.split('|')[
          this.widget.payload.prevTaxonomyLevels.length - 1
        ];
      if (!isRoot) {
        this.widget.payload.drilledDownOn = 'taxonomy';
        this.widget.payload.isDrilledDown = true;
      }
    }
  }

  public drilldownInto(
    item:
      | 'themes'
      | 'mentions'
      | 'taxonomy'
      | 'taxonomy_mentions'
      | 'topic_phrases'
      | 'entity_phrases'
      | 'taxonomy_phrases'
      | 'taxonomy_themes'
      | 'none',
    isDrillingDown: boolean,
    refreshOnDrillDown = false
  ): void {
    if (!this.widget.payload.prevTaxonomyLevels) {
      this.widget.payload.prevTaxonomyLevels = [];
    }
    if (item === 'topic_phrases' && this.widget.payload.type === 'taxonomyMap') {
      item = 'taxonomy_phrases';
    }
    if (!isDrillingDown) {
      switch (this.widget.payload.drilledDownOn) {
        case 'taxonomy':
          if (this.widget.payload.taxonomyPath && this.widget.payload.taxonomyPath.length > 0) {
            this.setTaxonomyLevels(item, true);
          } else {
            this.setTaxonomyLevels(item);
          }
          break;
        case 'taxonomy_mentions':
          this.setTaxonomyLevels(item);
          delete this.widget.payload.topLevelMentionItem;
          break;
        case 'taxonomy_phrases':
          delete this.widget.payload.topLevelMentionItem;
          this.setTaxonomyLevels(item);
          break;
        case 'taxonomy_themes':
          delete this.widget.payload.topLevelMentionItem;
          this.setTaxonomyLevels(item);
          break;
        default:
          this.widget.payload.isDrilledDown = isDrillingDown;
          this.widget.payload.drilledDownOn = item;
      }
    } else {
      this.widget.payload.isDrilledDown = isDrillingDown;
      this.widget.payload.drilledDownOn = item;
      this.widget.payload.drilledDownLocation = this.pointName;
    }

    //Push previous level of taxonomy on stack change label
    if (
      item === 'taxonomy' ||
      item === 'taxonomy_mentions' ||
      item === 'taxonomy_phrases' ||
      (item === 'taxonomy_themes' && isDrillingDown)
    ) {
      //Hacky bit for top level node mentions, phrases, themes
      if (
        (item === 'taxonomy_mentions' ||
          item === 'taxonomy_phrases' ||
          item === 'taxonomy_themes') &&
        this.widget.payload.prevTaxonomyLevels.length === 0
      ) {
        this.widget.payload.topLevelMentionItem = this.pointDisplayName;
      }
      //End hacky bit
      if (!this.widget.payload.taxonomyPath) {
        this.widget.payload.taxonomyPath = '';
      }
      this.widget.payload.prevTaxonomyLevels.push(this.widget.payload.taxonomyPath);

      //Handle the path to send based on the depth.
      //Depths >= 2 are saved in flatTaxonomy as parent|current,grandparent|parent|current, etc.
      if (item === 'taxonomy') {
        this.calculateTaxonomyPath();
      }
    }
    if (!refreshOnDrillDown) {
      this.refresh();
    }
  }

  public async goBackToRoot(): Promise<void> {
    this.widget.payload.taxonomyPath = '';
    this.widget.payload.prevTaxonomyLevels = [];
    this.widget.payload.isDrilledDown = false;
    this.widget.payload.drilledDownOn = 'none';
    this.widget.payload.drilledDownLocation = '';
    this.refresh();
  }

  public get displayTaxonomyPath() {
    const parts =
      typeof this.widget.payload.taxonomyPath === 'string' &&
      this.widget.payload.taxonomyPath.length > 0
        ? this.widget.payload.taxonomyPath.split('|')
        : [];
    if (parts.length <= 0) {
      return translate('widgets.root');
    }
    if (
      this.widget.payload.drilledDownOn === 'taxonomy_mentions' ||
      this.widget.payload.drilledDownOn === 'taxonomy_phrases' ||
      this.widget.payload.drilledDownOn === 'taxonomy_themes'
    ) {
      let drilledDownlabel = '';
      switch (this.widget.payload.drilledDownOn) {
        case 'taxonomy_mentions':
          drilledDownlabel = `${translate('widgets.mentions')}: `;
          break;
        case 'taxonomy_phrases':
          drilledDownlabel = `${translate('widgets.phrases')}: `;
          break;
        case 'taxonomy_themes':
          drilledDownlabel = `${translate('widgets.themes')}: `;
          break;
      }
      return (
        drilledDownlabel +
        this.widget.payload.taxonomyPath +
        '|' +
        this.widget.payload.drilledDownLocation
      );
    }
    return this.widget.payload.taxonomyPath;
  }

  public async showDocumentsForItem(_initialQuery?: string) {
    this.viewedDocument = true;

    this.widget.payload.analyticItem = 'taxonomy';
    this.widget.payload.prevTaxonomyLevels.push(this.widget.payload.taxonomyPath);

    const query = this.filterService.getQueryForDocuments(this.widgetQueryOptions);
    await super.showDocumentsForItem(query);
    this.resetTaxonomyLevel(true);
  }

  public openSmartSummary() {
    const queryString = this.filterService.getQueryForDocuments(this.widgetQueryOptions);
    super.openSmartSummary(this.pointName, queryString);
  }

  taxonomyFullPathPoint() {
    const pathMinusRoot = this.widget.payload.taxonomyPath?.replace(/Root\|?/g, '') || '';
    let pathSuffix = this.pointName;

    if (
      this.widget.payload.drilledDownOn === 'taxonomy_mentions' ||
      this.widget.payload.drilledDownOn === 'taxonomy_phrases' ||
      this.widget.payload.drilledDownOn === 'taxonomy_themes'
    ) {
      pathSuffix = this.widget.payload.drilledDownLocation;
    }

    return pathMinusRoot ? `${pathMinusRoot}|${pathSuffix}` : pathSuffix;
  }

  async exportAsImage() {
    return await this.chartDisplayService.exportChartAsImage(
      this.elementContent.nativeElement,
      this.displayTaxonomyPath
    );
  }

  public get widgetQueryOptions(): WidgetQueryOptions {
    const widgetQueryOptions = this.filterService.getWidgetQueryOptions(
      this.widget,
      this.widgetDataSource,
      this.widgetGroup,
      this.comparison?.filters[this.activeComparisonBucketIdx]
    );

    widgetQueryOptions.widgetContextMenu = this.widgetContextMenu;
    widgetQueryOptions.pointName = this.pointName;
    widgetQueryOptions.taxonomyPath = this.widget.payload.topLevelMentionItem
      ? this.widget.payload.topLevelMentionItem
      : this.widget.payload.taxonomyPath;
    widgetQueryOptions.analyticItem = 'taxonomy';
    widgetQueryOptions.subType = this.widgetContextMenu && this.widgetContextMenu.subType;
    widgetQueryOptions.parentPointName = this.parentPointName;
    widgetQueryOptions.analyticItems = this.analyticItems;

    return widgetQueryOptions;
  }

  public get pointSelected() {
    return this.point ? this.point['selected'] : null;
  }

  public async turnTaxonomyNodeIntoWordCloud() {
    const f: Filter = {
      analyticItem: 'taxonomy',
      key: 'node',
      value: this.taxonomyFullPathPoint(),
      displayName: this.pointDisplayName,
    };

    if (this.widget.payload.taxonomyPath) {
      f.subLabel = this.widget.payload.taxonomyPath;
    }

    let topicTypes = AnalyticItemTypes.defaultTopicTypes;
    const dataSource = this.widgetDataSource;
    if (dataSource.sourceVersion >= 6 && dataSource.topicTypes && dataSource.topicTypes.length) {
      topicTypes = dataSource.topicTypes;
      const finalTypes = [];
      const excludedTypes = ['clause_models'];
      topicTypes.forEach((item) => {
        if (!excludedTypes.includes(item.toLowerCase())) {
          finalTypes.push(item);
        }
      });
      topicTypes = finalTypes;
    }
    const widgetCopy = JSON.parse(
      JSON.stringify(this.widget, (_, v) => (v === undefined ? undefined : v))
    ); // preserve undefined values
    widgetCopy.id = UUIDv4();
    widgetCopy.component = 'DashboardWidgetChartComponent';
    widgetCopy.payload.filters.taxonomy.and.push(f);
    widgetCopy.payload.type = 'wordcloud';
    widgetCopy.payload.analyticItem = 'topics';
    widgetCopy.payload.entityTypeFilter = topicTypes.join();
    widgetCopy.payload.cloudFromTaxonomy = true;
    widgetCopy.title = `${translate('widgets.taxonomy')}: ${this.pointDisplayName}`;
    widgetCopy.payload.isDrilledDown = false;
    widgetCopy.payload.orderBySentiment = false;
    widgetCopy.payload.limit = 20;
    if (widgetCopy.payload.taxonomyId) {
      delete widgetCopy.payload.taxonomyId;
    }
    if (widgetCopy.payload.drilledDownOn) {
      delete widgetCopy.payload.drilledDownOn;
    }
    this.addWidgetToDashboardToRight(widgetCopy);
  }

  public get backToRoot() {
    return (
      (this.widget.payload.taxonomyPath && this.widget.payload.taxonomyPath.length > 0) ||
      (this.widget.payload.drilledDownOn &&
        (this.widget.payload.drilledDownOn === 'taxonomy_mentions' ||
          this.widget.payload.drilledDownOn === 'taxonomy_phrases' ||
          this.widget.payload.drilledDownOn === 'taxonomy_themes'))
    );
  }

  public searchTaxonomy() {
    const queryString = this.filterService.getQueryForDocuments(
      this.widgetQueryOptions,
      false,
      true
    );
    const filterString = this.filterService.buildFilterStringClearPolarityFilters(
      this.widget.payload.filters,
      this.widget.payload.analyticItem
    );
    this.dialogService
      .showDialog<
        TaxonomySearchDialogComponent,
        TaxonomySearchDialogOptions<TaxonomyMapWidgetOptions>
      >(
        TaxonomySearchDialogComponent,
        {
          widget: this.widget,
          widgetDataSource: this.widgetDataSource,
          projectId: this.widget.payload.projectId,
          reportFilter: filterString,
          reportQuery: queryString,
        },
        {},
        false
      )
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          if (result.widget) {
            this.widget = result.widget;
            this.refresh();
          }
        }
      });
  }

  public canShowDocumentList(): boolean {
    return false;
  }

  public canChangeAnalyticItem(): boolean {
    return false;
  }

  public hasMentions() {
    return super.hasMentions() && this.pointName !== 'UNCATEGORIZED';
  }

  public hasTopicPhrases() {
    return super.hasTopicPhrases() && this.pointName !== 'UNCATEGORIZED';
  }

  public hasTopicThemes() {
    return super.hasTopicThemes && this.pointName !== 'UNCATEGORIZED';
  }

  public hasPointDocuments(): boolean {
    return this.point && this.point.value !== 0;
  }

  public showMentionsToolTip(): string {
    if (this.pointName === 'UNCATEGORIZED') {
      return translate('widgets.not-available-for-this-item');
    }
    return super.showMentionsToolTip();
  }

  public showTopicPhrasesTooltip(): string {
    if (this.pointName === 'UNCATEGORIZED') {
      return translate('widgets.not-available-for-this-item');
    }
    return super.showTopicPhrasesTooltip();
  }

  public showTaxonomyThemesTooltip(): string {
    if (this.pointName === 'UNCATEGORIZED') {
      return translate('widgets.not-available-for-this-item');
    }
    return super.showTaxonomyThemesTooltip();
  }

  onCommentListOpenClicked(item) {
    this.contextMenuTriggerElement.closeMenu();
    setTimeout(() => super.onCommentListOpenClicked(item));
  }
}
